'use client'

import React from 'react'

import { chakra } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import {
  sliderInputStyles,
  sliderLabelStyles,
  sliderRootStyles,
} from './Slider.styles'

export type SliderProps = {
  value: number
  min: number
  max: number
  onChange: (value: number) => void
  /**
   * Fires once the drag is released rather than on every step. Persist from
   * here — `onChange` runs on each intermediate value, so writing from it
   * sends one request per step dragged.
   */
  onChangeEnd?: (value: number) => void
  step?: number
  label?: React.ReactNode
  disabled?: boolean
  colorScheme?: ColorScheme
  'aria-label'?: string
  'data-testid'?: string
}

const toPercent = (value: number, min: number, max: number) => {
  if (max <= min) return 0
  const clamped = Math.min(Math.max(value, min), max)
  return ((clamped - min) / (max - min)) * 100
}

export const Slider = ({
  value,
  min,
  max,
  onChange,
  onChangeEnd,
  step = 1,
  label,
  disabled = false,
  colorScheme = 'dark',
  'aria-label': ariaLabel,
  'data-testid': testId,
}: SliderProps) => {
  return (
    <chakra.div {...sliderRootStyles} opacity={disabled ? 0.5 : 1}>
      {label && (
        <chakra.label {...sliderLabelStyles(colorScheme)}>{label}</chakra.label>
      )}
      <chakra.input
        aria-label={ariaLabel}
        css={sliderInputStyles(colorScheme, toPercent(value, min, max))}
        data-testid={testId}
        disabled={disabled}
        max={max}
        min={min}
        onChange={(e) => onChange(Number(e.target.value))}
        // Covers both interaction paths: pointer release and keyboard.
        onKeyUp={(e) => onChangeEnd?.(Number(e.currentTarget.value))}
        onPointerUp={(e) => onChangeEnd?.(Number(e.currentTarget.value))}
        step={step}
        type='range'
        value={value}
      />
    </chakra.div>
  )
}
